<template>
  <div>
    <v-list-item link @click="launchResource()" :disabled="isLocked">
      <v-list-item-avatar
        :tile="resource.type === 'link' && !isCompleted"
        :size="resource.type === 'link' && !isLocked && !isCompleted ? 18 : null"
        :class="resource.type === 'link' && !isLocked && !isCompleted ? 'mx-3' : 'mx-2'">
        <v-icon class="grey--text" v-if="isLocked">mdi-lock</v-icon>
        <v-icon class="success--text" v-else-if="isCompleted">mdi-check-circle</v-icon>
        <v-icon v-else-if="resource.type === 'attachment'" :style="isActive ? activeStyle : inactiveStyle" :class="{ 'grey--text': !isActive }">{{ resourceIcon }}</v-icon>
        <v-img v-else :src="linkFaviconURL"></v-img>
      </v-list-item-avatar>
      <v-list-item-content>
        <v-list-item-subtitle>{{ resource.name }}</v-list-item-subtitle>
      </v-list-item-content>
    </v-list-item>
  </div>
</template>

<script>
import _ from 'lodash';
import {
  mapActions,
  mapState,
  mapMutations,
} from 'vuex';

export default {
  props: ['resource'],
  data: () => ({
    inactiveStyle: {
      color: 'gray',
    },
    resourceName: null,
    resourceType: null,
    shownNextItemToast: false,
  }),
  computed: {
    ...mapState([
      'activeModuleItemId',
      'activeResourceLinkURL',
      'adobePDFError',
      'allModuleItemAttempts',
      'courseHash',
      'downloadResourceURL',
      'nextItem',
      'PDFComplete',
      'playlist',
      'requiredItemIndex',
      'snackbar',
      'user',
    ]),
    activeResourceLinkIsPDF() {
      if (typeof this.activeResourceLinkURL === 'string') return _.get(this, 'activeResourceLinkURL', []).includes('.pdf');
      return false;
    },
    activeStyle() {
      return { color: this.playlist.default_color };
    },
    isActive() {
      return this.activeModuleItemId === this.resource.itemId;
    },
    isCompleted() {
      return !!_.get(this.moduleItemAttempt, 'complete', false);
    },
    isLocked() {
      if (
        _.get(this.playlist, 'config.lockProgression')
        && this.resourceIndex > this.requiredItemIndex
        && (this.resource.itemId !== this.nextItem.itemId)
        && this.user.userType !== 'teacher') {
        return true;
      }
      return false;
    },
    linkFaviconURL() {
      return `https://www.google.com/s2/favicons?domain=${_.get(this.resource, 'link.url', '')}`;
    },
    moduleItemAttempt() {
      return _.find(this.allModuleItemAttempts, { moduleItemId: this.resource.id });
    },
    resourceIcon() {
      if (this.resource.type === 'link') return 'mdi-link';
      if (this.resourceType.includes('pdf')) return 'mdi-pdf-box';
      if (this.resourceType.includes('image')) return 'mdi-image';
      if (this.resourceName.includes('doc')) return 'mdi-file-document-outline';
      return 'mdi-paperclip';
    },
    resourceIndex() {
      return _.findIndex(this.playlist.allModuleItems, { itemId: this.resource.itemId });
    },
  },
  methods: {
    ...mapActions([
      'markModuleItemAttemptComplete',
      'setModuleItem',
    ]),
    ...mapMutations([
      'forceCloseSnackbar',
      'setActiveModuleItemId',
      'setActiveResourceLinkURL',
      'setDownloadResourceURL',
      'setNextItemTimeoutToast',
      'toggleSnackbar',
    ]),
    launchResource() {
      this.setNextItemTimeoutToast(null);
      this.setModuleItem(this.resource);
      if (
        _.get(this.snackbar, 'open')
        && (
          this.resource.itemId !== this.activeModuleItemId
          && _.get(this.playlist, 'config.lockProgression')
        )
      ) this.forceCloseSnackbar();
      if (this.resource.type === 'link') {
        this.setActiveResourceLinkURL(this.resource);
      } else if (this.resourceType.includes('pdf') || this.resourceType.includes('image')) {
        this.setActiveResourceLinkURL(this.resource.attachment.url);
      } else {
        this.setDownloadResourceURL(`${this.resource.attachment.url}?bcdn_filename=${encodeURIComponent(this.resource.name)}`);
      }
      const path = `/course/${this.courseHash}/module/${this.resource.moduleId}/item/${this.resource.itemId}`;
      if (this.$route.path !== path) {
        this.$router.push({
          name: 'Course Home',
          params: { courseHash: this.courseHash, moduleId: this.resource.moduleId, itemId: this.resource.itemId },
        });
      }
    },
    popNextItemToast(timeout = 5000) {
      if (_.get(this.user, 'userType', '') === 'teacher') return;
      if (
        this.resource.type === 'link'
        && timeout !== -1
      ) timeout = 1000;
      if (this.PDFComplete) this.markModuleItemAttemptComplete(this.resource.id);
      const toastTimeout = setTimeout(() => {
        this.shownNextItemToast = true;
        const lastModuleItemIndex = this.playlist.allModuleItems.length - 1;
        const currentModuleItemIndex = _.findIndex(this.playlist.allModuleItems, { itemId: this.activeModuleItemId });
        if (currentModuleItemIndex === lastModuleItemIndex) {
          this.snackbarAction = false;
        } else {
          this.snackbarAction = true;
        }
        if (!this.PDFComplete) this.markModuleItemAttemptComplete(this.resource.id);
        this.toggleSnackbar({
          text: 'Item completed!',
          color: 'success',
          action: this.snackbarAction,
          timeout: this.snackbarAction ? -1 : 5000,
        });
      }, timeout);
      this.setNextItemTimeoutToast(toastTimeout);
    },
  },
  created() {
    this.resourceName = _.get(this.resource, 'attachment.name') || _.get(this.resource, 'name', '');
    this.resourceType = _.get(this.resource, 'attachment.type', '');
    if (
      this.isActive
      && !this.shownNextItemToast
      && this.activeResourceLinkURL
      && (
        !this.activeResourceLinkIsPDF
        || this.adobePDFError
      )
      && !_.isEmpty(this.activeResourceLinkURL)
      && !this.downloadResourceURL
      && this.user.userType !== 'teacher'
    ) this.popNextItemToast();
  },
  watch: {
    isActive(n) {
      if (
        n
        && !this.shownNextItemToast
        && this.activeResourceLinkURL
        && (
          !this.activeResourceLinkIsPDF
          || this.adobePDFError
        )
        && !_.isEmpty(this.activeResourceLinkURL)
        && !this.downloadResourceURL
      ) this.popNextItemToast();
      // Resource is complete and user didn't click Next Item toast in lockedProgression playlist - re-pop toast
      if (
        n
        && this.isCompleted
        && this.requiredItemIndex === this.resourceIndex
        && _.get(this.playlist, 'config.lockProgression')
      ) this.popNextItemToast(0);
    },
    PDFComplete(n) {
      if (
        n
        && this.isActive
      ) this.popNextItemToast(0);
    },
    adobePDFError(n) {
      if (
        n
        && this.isActive
        && !this.shownNextItemToast
      ) this.popNextItemToast(0);
    },
  },
};
</script>

<style lang="scss">
  .v-list-item__subtitle {
    /* Overrides Vuetify styling on li elements to wrap longer bulb names */
    overflow: visible !important;
    white-space: normal !important;
  }
</style>
